import { LayerKind, type PlanLayers } from "@/shared/plan";
import { exportObjectMarks } from "./exportMarks";

const DRAWING_AREA = { width: 1052, height: 620 } as const;
const DRAWING_PADDING = 80;

interface LabelBox {
	x: number;
	y: number;
	width: number;
	height: number;
}

const overlaps = (a: LabelBox, b: LabelBox) =>
	a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;

const createSvg = (tag: string) => document.createElementNS("http://www.w3.org/2000/svg", tag);

const addObjectMarks = (
	source: SVGSVGElement,
	svg: SVGSVGElement,
	layers: PlanLayers,
	markedLayers: readonly LayerKind[] | undefined,
) => {
	const marks = exportObjectMarks(layers, markedLayers);
	const viewBox = source.viewBox.baseVal;
	const sourceBounds = source.getBoundingClientRect();
	const occupied: LabelBox[] = [];
	for (const target of svg.querySelectorAll<SVGElement>("[data-role][data-id]")) {
		const mark = marks.get(target.dataset.id ?? "");
		if (!mark) continue;
		const sourceTarget = source.querySelector<SVGElement>(`[data-id="${target.dataset.id}"]`);
		if (!sourceTarget || sourceBounds.width === 0 || sourceBounds.height === 0) continue;
		const targetBounds = sourceTarget.getBoundingClientRect();
		const center = {
			x:
				viewBox.x +
				((targetBounds.left + targetBounds.width / 2 - sourceBounds.left) / sourceBounds.width) *
					viewBox.width,
			y:
				viewBox.y +
				((targetBounds.top + targetBounds.height / 2 - sourceBounds.top) / sourceBounds.height) *
					viewBox.height,
		};
		const width = mark.length * 55 + 70;
		const height = 110;
		const candidates = [
			{ x: center.x + 130, y: center.y - 190 },
			{ x: center.x - width - 130, y: center.y - 190 },
			{ x: center.x + 130, y: center.y + 80 },
			{ x: center.x - width - 130, y: center.y + 80 },
		];
		const box = candidates
			.map((candidate) => ({ ...candidate, width, height }))
			.find((candidate) => !occupied.some((placed) => overlaps(candidate, placed))) ?? {
			x: center.x + 130,
			y: center.y + occupied.length * 130,
			width,
			height,
		};
		occupied.push(box);
		const line = createSvg("line");
		line.setAttribute("x1", String(center.x));
		line.setAttribute("y1", String(center.y));
		line.setAttribute("x2", String(box.x < center.x ? box.x + width : box.x));
		line.setAttribute("y2", String(box.y + height / 2));
		line.setAttribute("stroke", "#1d4ed8");
		line.setAttribute("stroke-width", "14");
		const frame = createSvg("rect");
		frame.setAttribute("x", String(box.x));
		frame.setAttribute("y", String(box.y));
		frame.setAttribute("width", String(width));
		frame.setAttribute("height", String(height));
		frame.setAttribute("rx", "16");
		frame.setAttribute("fill", "#ffffff");
		frame.setAttribute("stroke", "#1d4ed8");
		frame.setAttribute("stroke-width", "10");
		const label = createSvg("text");
		label.setAttribute("x", String(box.x + width / 2));
		label.setAttribute("y", String(box.y + height * 0.7));
		label.setAttribute("text-anchor", "middle");
		label.setAttribute("font-size", "76");
		label.setAttribute("font-weight", "700");
		label.setAttribute("fill", "#1d4ed8");
		label.textContent = mark;
		svg.append(line, frame, label);
	}
};

/** Снимает готовый SVG 2D-редактора для последующего векторного листа PDF. */
export const planSvg = (
	layers: PlanLayers,
	markedLayers?: readonly LayerKind[],
	wallsOnly = false,
): SVGSVGElement => {
	const source = document.querySelector<SVGSVGElement>("svg[data-plan-export]");
	if (!source) throw new Error("Не найден чертёж для экспорта.");

	const svg = source.cloneNode(true) as SVGSVGElement;
	svg.querySelector("[data-plan-export-grid]")?.remove();
	svg.querySelectorAll("[data-plan-export-exclude]").forEach((element) => element.remove());
	svg.querySelectorAll('[fill="transparent"]').forEach((element) => element.remove());
	if (wallsOnly) {
		svg.querySelectorAll('[data-role="comment"]').forEach((element) => {
			element.closest("g")?.remove();
		});
	}
	// Что спрятал масштаб экрана (уровни детализации), на листе нужно: показываем
	svg.querySelectorAll<SVGElement>("[data-lod-hidden]").forEach((element) => {
		element.removeAttribute("data-lod-hidden");
		element.style.opacity = "";
		element.style.transition = "";
		element.style.pointerEvents = "";
	});
	addObjectMarks(source, svg, layers, markedLayers);
	const nodes = Object.values(layers.walls.nodes);
	if (nodes.length > 0) {
		const technicalMargin = 250;
		const minX = Math.min(...nodes.map((node) => node.x)) - technicalMargin;
		const minY = Math.min(...nodes.map((node) => node.y)) - technicalMargin;
		const maxX = Math.max(...nodes.map((node) => node.x)) + technicalMargin;
		const maxY = Math.max(...nodes.map((node) => node.y)) + technicalMargin;
		const { width: drawingWidth, height: drawingHeight } = DRAWING_AREA;
		const millimetersPerPixel = Math.max(
			(maxX - minX) / (drawingWidth - DRAWING_PADDING * 2),
			(maxY - minY) / (drawingHeight - DRAWING_PADDING * 2),
		);
		const viewBoxWidth = drawingWidth * millimetersPerPixel;
		const viewBoxHeight = drawingHeight * millimetersPerPixel;
		const centerX = (minX + maxX) / 2;
		const centerY = (minY + maxY) / 2;
		svg.setAttribute(
			"viewBox",
			`${centerX - viewBoxWidth / 2} ${centerY - viewBoxHeight / 2} ${viewBoxWidth} ${viewBoxHeight}`,
		);
	}
	svg.removeAttribute("class");
	svg.removeAttribute("style");
	svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
	return svg;
};
